import React from 'react';

import { AlertFilled, ErrorFilled, InformationFilled, TickInCircleFilled } from '@swyftx/aviary/icons/filled';

import { cn } from '@shared/utils/lib/ui';

import * as Toast from '@radix-ui/react-toast';

import { SnackBarProps, snackBarVariants } from './Snackbar.styles';
import { Button, ButtonProps } from '../Button';
import { FlexLayout } from '../Layout/Flex';
import { Body } from '../Typography';

const SnackBar = React.forwardRef<HTMLLIElement, SnackBarProps>(
  ({ variant = 'default', showIcon, label, onClick, content, duration, open, className, onOpenChange, id }, ref) => {
    const icon = React.useMemo(() => {
      switch (variant) {
        case 'default':
        case 'info':
          return <InformationFilled className='text-color-text-inverse' />;
        case 'success':
          return <TickInCircleFilled className='text-color-text-inverse' />;
        case 'warning':
          return <AlertFilled className='text-color-text-inverse' />;
        case 'error':
          return <ErrorFilled className='text-color-text-inverse' />;
      }
    }, [variant]);

    const buttonColor = React.useMemo((): ButtonProps['color'] => {
      switch (variant) {
        case 'default':
          return 'primary';
        case 'info':
        case 'success':
        case 'warning':
        case 'error':
          return 'inverse';
        default:
          variant satisfies never;
      }
    }, [variant]);

    return (
      <Toast.Provider swipeDirection='down'>
        <Toast.Root
          id={id}
          data-testid={id}
          ref={ref}
          duration={duration}
          className={cn(
            snackBarVariants({ variant }),
            className,
            'data-[swipe=end]:animate-swipeOut data-[state=closed]:animate-snackbarExit data-[state=open]:animate-snackbarEnter  data-[swipe=cancel]:translate-y-0 data-[swipe=move]:translate-y-[var(--radix-toast-swipe-move-y)] data-[swipe=cancel]:transition-[transform_200ms_ease-out]',
          )}
          open={open}
          onOpenChange={onOpenChange}
        >
          <FlexLayout alignItems='center' className='w-full' justifyContent='space-between' spacing={24}>
            <Toast.Title>
              <FlexLayout alignItems='center' justifyContent='start' spacing={12}>
                {showIcon && icon}
                <Body color='inverse' weight='emphasis'>
                  {content}
                </Body>
              </FlexLayout>
            </Toast.Title>

            {label && onClick && (
              <Toast.Action className='[grid-area:_action]' asChild altText={label}>
                <Button color={buttonColor} variant='filled' onClick={onClick}>
                  {label}
                </Button>
              </Toast.Action>
            )}
          </FlexLayout>
        </Toast.Root>
        <Toast.Viewport className='fixed bottom-0 left-1/2 z-[2147483647] m-0 flex w-full -translate-x-1/2 list-none flex-col gap-2.5 p-[var(--viewport-padding)] outline-none [--viewport-padding:_25px] sm:min-w-[450px] sm:max-w-[450px]' />
      </Toast.Provider>
    );
  },
);

SnackBar.displayName = 'SnackBar';

export { SnackBar };
